import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Package, Trash2, ChevronDown, ChevronUp, CheckCircle2, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { brl, pct, num, weekLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Badge, Button, EmptyState, ErrorState, Loading, Page, PageHeader } from "@/components/kit";

function ConferenceCard({ c }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const s = c.summary;
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="cursor-pointer" style={{ padding: "var(--pad)" }} onClick={() => nav(`/conference/${c.id}`)} data-testid={`conference-card-${c.id}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="h3">{weekLabel(c)}</div>
          <div className="flex gap-2">
            {c.above_target && <Badge tone="warning">Acima da meta</Badge>}
            <Badge tone={c.status === "aberta" ? "success" : "muted"}>{c.status === "aberta" ? "Aberta" : "Finalizada"}</Badge>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div><div className="caption">Faturamento</div><div className="label tnum">{brl(c.revenue)}</div></div>
          <div><div className="caption">CMV</div><div className="label tnum">{brl(s.cmv)}</div></div>
          <div className="text-right"><div className="caption">CMV %</div><div className="h3 tnum" style={{ color: c.above_target ? "var(--accent)" : "var(--success)" }}>{pct(s.pct)}</div></div>
        </div>
        <div className="caption mt-3 flex items-center gap-1.5"><Package size={13} /> {s.total} produtos · {s.counted} contados{s.pending ? ` · ${s.pending} pendentes` : ""}</div>
        {s.losses_total > 0 && (
          <div className="caption mt-1 flex items-center gap-1.5" data-testid={`conference-losses-${c.id}`}><Trash2 size={13} /> Perdas {brl(s.losses_total)} · consumo normal {brl(s.normal_consumption)}</div>
        )}
      </div>
      <button className="w-full flex items-center justify-center gap-1 py-2.5 border-t label muted" style={{ borderColor: "var(--border)" }} onClick={() => setOpen(!open)} data-testid={`conference-toggle-items-${c.id}`}>
        {open ? "Ocultar itens" : "Ver itens e perdas"} {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>
      {open && (
        <div className="px-4 pb-4 flex flex-col gap-1.5 fade-in" data-testid={`conference-items-${c.id}`}>
          {c.items_preview.length === 0 && <p className="caption">Nenhum item.</p>}
          {c.items_preview.map((i) => (
            <div key={i.product_id} className="flex items-center gap-2 text-sm">
              {i.counted ? <CheckCircle2 size={14} style={{ color: "var(--success)" }} /> : <Circle size={14} className="muted" />}
              <span className="flex-1 truncate">{i.product_name}</span>
              <span className="muted tnum">{i.counted ? `${num(i.final_qty)} ${i.unit}` : "pendente"}</span>
            </div>
          ))}
          {c.losses_preview.length > 0 && <div className="eyebrow mt-3">Perdas ({c.losses_preview.length})</div>}
          {c.losses_preview.map((l) => (
            <div key={l.id} className="flex gap-2 text-sm"><span className="flex-1 truncate">{l.product_name} · {l.reason}</span><span className="muted tnum">{num(l.qty)} {l.unit} · {brl(l.value)}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Conferences() {
  const nav = useNavigate();
  const { isMgmt } = useAuth();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["conferences"], queryFn: () => api("/conferences") });
  return (
    <Page>
      <PageHeader title="Conferências" subtitle="Contagem de estoque por período · CMV = inicial + compras − final"
        right={isMgmt && <Button icon={Plus} onClick={() => nav("/conferences/new")} data-testid="new-conference-button" className="hidden sm:inline-flex">Nova conferência</Button>} />
      {isLoading ? <Loading /> : isError ? <ErrorState message={error.message} onRetry={refetch} /> : data.length === 0 ? (
        <EmptyState title="Nenhuma conferência" text={isMgmt ? "Toque em “Nova conferência” para iniciar a contagem do período." : "Aguarde o administrador abrir a conferência da semana."} />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="conferences-list">
          {data.map((c) => <ConferenceCard key={c.id} c={c} />)}
        </div>
      )}
      {isMgmt && (
        <button className="sm:hidden fixed right-5 bottom-24 z-20 btn btn-primary shadow-xl" style={{ borderRadius: 999 }} onClick={() => nav("/conferences/new")} data-testid="new-conference-fab">
          <Plus size={18} /> Nova conferência
        </button>
      )}
    </Page>
  );
}
